import { logEvent } from 'firebase/analytics'
import { analytics } from '../firebaseConfig'
import { CANDIDATE_FINDER_SCORING_VERSION } from './scoring'

export const CANDIDATE_FINDER_ATTRIBUTION_KEY = 'scribeshadow:candidate-finder-attribution:v1'
export const CANDIDATE_FINDER_ORIGIN = 'translation_candidate_finder'

export interface CandidateFinderAttribution {
  originFeature: typeof CANDIDATE_FINDER_ORIGIN
  source?: string
  medium?: string
  campaign?: string
  content?: string
  capturedAt: string
  signupIntentAt?: string
}

const sanitizeCampaignValue = (value: string | null): string | undefined => {
  if (!value) return undefined
  if (/@|https?:\/\/|www\./i.test(value)) return undefined
  const sanitized = value
    .trim()
    .replace(/[^a-zA-Z0-9._~ -]/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, 100)
  return sanitized || undefined
}

const safeSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch (_error) {
    return null
  }
}

export const readCandidateFinderAttribution = (): CandidateFinderAttribution | null => {
  const storage = safeSessionStorage()
  if (!storage) return null
  try {
    const raw = storage.getItem(CANDIDATE_FINDER_ATTRIBUTION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CandidateFinderAttribution>
    if (parsed.originFeature !== CANDIDATE_FINDER_ORIGIN || typeof parsed.capturedAt !== 'string') {
      return null
    }
    return {
      originFeature: CANDIDATE_FINDER_ORIGIN,
      capturedAt: parsed.capturedAt,
      ...(sanitizeCampaignValue(parsed.source || null) ? { source: sanitizeCampaignValue(parsed.source || null) } : {}),
      ...(sanitizeCampaignValue(parsed.medium || null) ? { medium: sanitizeCampaignValue(parsed.medium || null) } : {}),
      ...(sanitizeCampaignValue(parsed.campaign || null) ? { campaign: sanitizeCampaignValue(parsed.campaign || null) } : {}),
      ...(sanitizeCampaignValue(parsed.content || null) ? { content: sanitizeCampaignValue(parsed.content || null) } : {}),
      ...(typeof parsed.signupIntentAt === 'string' ? { signupIntentAt: parsed.signupIntentAt } : {}),
    }
  } catch (_error) {
    return null
  }
}

export const captureCandidateFinderAttribution = (search: string): CandidateFinderAttribution => {
  const existing = readCandidateFinderAttribution()
  const params = new URLSearchParams(search)
  const captured: CandidateFinderAttribution = {
    originFeature: CANDIDATE_FINDER_ORIGIN,
    capturedAt: existing?.capturedAt || new Date().toISOString(),
    ...(sanitizeCampaignValue(params.get('utm_source')) || existing?.source
      ? { source: sanitizeCampaignValue(params.get('utm_source')) || existing?.source }
      : {}),
    ...(sanitizeCampaignValue(params.get('utm_medium')) || existing?.medium
      ? { medium: sanitizeCampaignValue(params.get('utm_medium')) || existing?.medium }
      : {}),
    ...(sanitizeCampaignValue(params.get('utm_campaign') || params.get('campaign')) || existing?.campaign
      ? { campaign: sanitizeCampaignValue(params.get('utm_campaign') || params.get('campaign')) || existing?.campaign }
      : {}),
    ...(sanitizeCampaignValue(params.get('utm_content')) || existing?.content
      ? { content: sanitizeCampaignValue(params.get('utm_content')) || existing?.content }
      : {}),
    ...(existing?.signupIntentAt ? { signupIntentAt: existing.signupIntentAt } : {}),
  }

  const storage = safeSessionStorage()
  if (storage) {
    try {
      storage.setItem(CANDIDATE_FINDER_ATTRIBUTION_KEY, JSON.stringify(captured))
    } catch (_error) {
      // Attribution is best-effort and must never block the public tool.
    }
  }
  return captured
}

export const markCandidateFinderSignupIntent = (): void => {
  const attribution = readCandidateFinderAttribution() || captureCandidateFinderAttribution('')
  const storage = safeSessionStorage()
  if (!storage) return
  try {
    storage.setItem(CANDIDATE_FINDER_ATTRIBUTION_KEY, JSON.stringify({
      ...attribution,
      signupIntentAt: new Date().toISOString(),
    }))
  } catch (_error) {
    // Signup attribution is best-effort.
  }
}

export const candidateFinderSignupFields = (): {
  signupOriginFeature?: string
  signupCampaignSource?: string
  signupCampaignMedium?: string
  signupCampaignName?: string
  signupCampaignContent?: string
} => {
  const attribution = readCandidateFinderAttribution()
  if (!attribution?.signupIntentAt) return {}
  return {
    signupOriginFeature: attribution.originFeature,
    ...(attribution.source ? { signupCampaignSource: attribution.source } : {}),
    ...(attribution.medium ? { signupCampaignMedium: attribution.medium } : {}),
    ...(attribution.campaign ? { signupCampaignName: attribution.campaign } : {}),
    ...(attribution.content ? { signupCampaignContent: attribution.content } : {}),
  }
}

const analyticsAttributionFields = (): Record<string, string> => {
  const attribution = readCandidateFinderAttribution()
  if (!attribution) return {}
  return {
    origin_feature: attribution.originFeature,
    ...(attribution.source ? { campaign_source: attribution.source } : {}),
    ...(attribution.medium ? { campaign_medium: attribution.medium } : {}),
    ...(attribution.campaign ? { campaign_name: attribution.campaign } : {}),
  }
}

export const trackCandidateFinderEvent = (
  eventName: 'candidate_finder_started' | 'candidate_finder_completed' | 'candidate_finder_cta_clicked',
  parameters: Record<string, string | number | boolean>,
): void => {
  if (!analytics) return
  try {
    logEvent(analytics, eventName, {
      scoring_version: CANDIDATE_FINDER_SCORING_VERSION,
      ...analyticsAttributionFields(),
      ...parameters,
    })
  } catch (_error) {
    // Analytics is non-critical and should never interrupt the assessment.
  }
}
